import { FCX, usePlatformFeatureFlag } from "@datassential/platform-ui-lib";
import useCategoryProfileTrendingKeywords, { TrendingKeyword } from "@apiHooks/useCategoryProfileTrendingKeywords";
import { iReportProPublication } from "@models/ReportProPublication";
import CategoryProfileSectionTitle from "@pages/CategoryProfile/components/CategoryProfileSectionTitle/CategoryProfileSectionTitle";
import { CategoryProfileSectionComponentProps } from "@pages/CategoryProfile/CategoryProfile.constants";
import TrendingNowMacs from "./components/TrendingNowMacs/TrendingNowMacs";
import TrendingNowChart from "./components/TrendingNowChart/TrendingNowChart";
import { useCategoryProfileMacs } from "@apiHooks/useCategoryProfileMacs";
import { useCallback } from "react";
import http from "@core/fetch";
import apiConfig from "@apiConfig";
import { useParams } from "react-router-dom";

export interface TrendingNowSectionData {
    keywords: TrendingKeyword[];
    macs: iReportProPublication[];
    categoryName: string;
}

const TRENDING_KEYWORDS_FEATURE_FLAG = 'SNAP Category Profile Trending Now';

export const useTrendingNowSectionData = (categoryId: string, categoryName: string = "") => {
    const areKeywordsEnabled = usePlatformFeatureFlag(TRENDING_KEYWORDS_FEATURE_FLAG);

    const { data: keywords, isLoading: isKeywordsLoading } = useCategoryProfileTrendingKeywords(areKeywordsEnabled ? categoryId : "");
    const { data: { items: macs }, isLoading: isMacsLoading } = useCategoryProfileMacs(categoryName);

    const isLoading = isMacsLoading || (areKeywordsEnabled && isKeywordsLoading);
    const hasData = macs.length > 0 || (areKeywordsEnabled && keywords.length > 0);

    return { hasData, isLoading, data: { keywords, macs, categoryName } };
};

const CategoryProfileTrendingNow: FCX<CategoryProfileSectionComponentProps> = ({ title, data }) => {
    const { id: categoryId } = useParams<{ id: string }>();

    const { keywords = [], macs = [], categoryName = "" } = data as TrendingNowSectionData;

    const handleDownload = useCallback(
        () => http.downloadFileXHRFromUrl(
            'GET',
            `${apiConfig['get category profile trending now report']}?categoryProfileId=${categoryId}`,
        ),
        [categoryId]
    );

    const hasKeywords = keywords.length > 0;

    return (
        <div className="CategoryProfile__section-content" data-testid="blockCategoryProfileTrendingNow">
            <CategoryProfileSectionTitle
                title={title}
                // The report is built around the keyword chart, so it follows its visibility.
                onDownload={hasKeywords ? handleDownload : undefined}
            />
            {hasKeywords && (
                <TrendingNowChart keywords={keywords} categoryName={categoryName} />
            )}
            {macs.length > 0 && <TrendingNowMacs macs={macs} />}
        </div>
    );
};

export default CategoryProfileTrendingNow;
